import React, { useEffect, useState } from "react";
import { Col, Form, Input, InputNumber, Modal, Row, Select, Table, Tag, Tooltip } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import SvgIcon from "../../const/SvgIcon";
import LoadingButton from "../../component/LoadingButton";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import {
  fetchOrganizationIntegrations,
  saveOrganizationIntegration,
} from "../../redux/Services/OrganizationServices";

// Starting body for a new integration: every variable under "Data"
const defaultBody = (variables = []) =>
  JSON.stringify(
    { Data: Object.fromEntries(variables.map((v) => [v, `{{${v}}}`])) },
    null,
    2
  );

/**
 * Partner APIs of one organization (create / update patient, OTP, ...).
 * A type without an active configuration keeps the built-in behaviour.
 * `organizationId` is the organization's row id (Organization.Id).
 */
const IntegrationsSection = ({ organizationId }) => {
  const [catalog, setCatalog] = useState({ Types: [], Methods: [], Integrations: [] });
  const [loading, setLoading] = useState(false);
  // null: closed, otherwise the type being edited
  const [editingType, setEditingType] = useState(null);
  const [form] = Form.useForm();

  const load = async () => {
    if (!organizationId) return;
    setLoading(true);
    const data = await fetchOrganizationIntegrations(organizationId);
    if (data) setCatalog(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [organizationId]);

  const byType = Object.fromEntries(catalog.Integrations.map((i) => [i.Type, i]));

  const openForm = (type) => {
    const saved = byType[type.Type];
    setEditingType(type);
    form.setFieldsValue(
      saved
        ? { ...saved }
        : {
            Type: type.Type,
            Method: "POST",
            Url: "",
            Headers: [{ Key: "Content-Type", Value: "application/json" }],
            BodyTemplate: defaultBody(type.Variables),
            ResponseMap: type.ResponseFields.map((f) => ({ Field: f.name, Path: "" })),
            SuccessPath: "",
            SuccessValue: "",
            TimeoutMs: 30000,
            Status: 1,
          }
    );
  };

  const [handleSave, saving] = useAsyncAction(async (values) => {
    const res = await saveOrganizationIntegration(organizationId, {
      ...values,
      Type: editingType.Type,
    });
    if (res) {
      setEditingType(null);
      load();
    }
  });

  const columns = [
    {
      title: "API",
      key: "label",
      render: (_, type) => (
        <>
          <strong>{type.Label}</strong>
          <div className="fs-12">{type.Description}</div>
        </>
      ),
    },
    {
      title: "Status",
      key: "status",
      className: "table_status_col",
      render: (_, type) => {
        const saved = byType[type.Type];
        if (!saved) return <Tag>Not configured</Tag>;
        return saved.Status ? <Tag color="green">Active</Tag> : <Tag color="orange">Inactive</Tag>;
      },
    },
    {
      title: "Endpoint",
      key: "endpoint",
      render: (_, type) => {
        const saved = byType[type.Type];
        return saved ? (
          <span style={{ wordBreak: "break-all" }}>
            <Tag>{saved.Method}</Tag>
            {saved.Url}
          </span>
        ) : (
          "Built-in behaviour"
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      className: "table_action_col",
      render: (_, type) => (
        <Tooltip title="Configure">
          <span className="cursor-pointer" onClick={() => openForm(type)}>
            <SvgIcon.Edit className="scale-icon edit-icon" />
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <h3>Partner API Integrations</h3>
      <p className="mb-10">
        When an API is not configured (or inactive), the built-in behaviour is used. When it is
        active and the partner call fails, the request fails and nothing is saved.
      </p>
      <div className="tableResponsive mt-10">
        <Table
          rowKey="Type"
          loading={loading}
          className="table antd_table wrap_table"
          bordered
          columns={columns}
          dataSource={catalog.Types}
          pagination={false}
        />
      </div>

      <Modal
        title={editingType ? `Configure: ${editingType.Label}` : ""}
        open={!!editingType}
        onCancel={() => setEditingType(null)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Form form={form} layout="vertical" autoComplete="off" onFinish={handleSave}>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={6}>
              <Form.Item label="Method" name="Method" rules={[{ required: true }]}>
                <Select options={catalog.Methods.map((m) => ({ value: m, label: m }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={18}>
              <Form.Item
                label="URL"
                name="Url"
                rules={[{ required: true, whitespace: true, message: "URL is required" }]}
              >
                <Input placeholder="https://partner.example.com/api/patients" />
              </Form.Item>
            </Col>
          </Row>

          <label className="fw-500">Headers</label>
          <p className="fs-12 mb-10">Saved values are hidden as ******** ; leave them as is to keep them.</p>
          <Form.List name="Headers">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Row gutter={[8, 0]} key={key} align="middle">
                    <Col xs={10}>
                      <Form.Item name={[name, "Key"]} rules={[{ required: true, message: "Name required" }]}>
                        <Input placeholder="api-key" />
                      </Form.Item>
                    </Col>
                    <Col xs={12}>
                      <Form.Item name={[name, "Value"]} rules={[{ required: true, message: "Value required" }]}>
                        <Input placeholder="Value" />
                      </Form.Item>
                    </Col>
                    <Col xs={2}>
                      <MinusCircleOutlined className="mb-20" onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Form.Item>
                  <span className="cursor-pointer" onClick={() => add({ Key: "", Value: "" })}>
                    <PlusOutlined /> Add header
                  </span>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item
            label="Body template"
            name="BodyTemplate"
            extra={
              <>
                Variables:{" "}
                {(editingType?.Variables || []).map((v) => (
                  <Tag key={v} className="mt-5">{`{{${v}}}`}</Tag>
                ))}
                <div className="mt-5">
                  Put placeholders in quotes in JSON. A value that is exactly "{"{{name}}"}" keeps its
                  type (number, etc.). Placeholders also work in the URL and header values.
                </div>
              </>
            }
          >
            <Input.TextArea rows={10} style={{ fontFamily: "monospace" }} />
          </Form.Item>

          <label className="fw-500">Response mapping</label>
          <p className="fs-12 mb-10">
            Field to read from the partner response, by path (e.g. data.pId).
            {editingType?.ResponseFields?.length
              ? ` Required: ${editingType.ResponseFields
                  .filter((f) => f.required)
                  .map((f) => `${f.name} (${f.description})`)
                  .join(", ")}`
              : " Not needed for this API."}
          </p>
          <Form.List name="ResponseMap">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Row gutter={[8, 0]} key={key} align="middle">
                    <Col xs={10}>
                      <Form.Item name={[name, "Field"]} rules={[{ required: true, message: "Field required" }]}>
                        <Input placeholder="pId" />
                      </Form.Item>
                    </Col>
                    <Col xs={12}>
                      <Form.Item name={[name, "Path"]} rules={[{ required: true, message: "Path required" }]}>
                        <Input placeholder="data.pId" />
                      </Form.Item>
                    </Col>
                    <Col xs={2}>
                      <MinusCircleOutlined className="mb-20" onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Form.Item>
                  <span className="cursor-pointer" onClick={() => add({ Field: "", Path: "" })}>
                    <PlusOutlined /> Add mapping
                  </span>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Success check path"
                name="SuccessPath"
                extra="Optional, e.g. statusCode. Empty: any 2xx response is success."
              >
                <Input placeholder="statusCode" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Success value" name="SuccessValue">
                <Input placeholder="0" />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item label="Timeout (ms)" name="TimeoutMs">
                <InputNumber min={1000} max={120000} step={1000} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item label="Status" name="Status">
                <Select>
                  <Select.Option value={1}>Active</Select.Option>
                  <Select.Option value={0}>Inactive</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div className="text-center">
            <LoadingButton type="submit" className="gradient-button w-auto" loading={saving}>
              Save
            </LoadingButton>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default IntegrationsSection;
